/**
 * Onboarding API Service
 * Profile creation during onboarding flow
 */

import type {
	ContractorProjectType,
	DesignerProjectType,
	HomeownerGoal,
	HomeownerPropertyType,
	PropertyStatus,
	RealtorClientType,
	RealtorPropertyType,
} from "@shared/types";
import type {
	ContractorProfile,
	DesignerProfile,
} from "@/features/marketplace/services/marketplace-api.service";
import type { HomeownerProfile } from "@/features/settings/services/settings-api.service";
import { apiClient } from "@/lib/api-client";

const API_BASE = "/api/v1";

// ============================================================================
// TYPES
// ============================================================================

/**
 * Discriminated result type for create-profile calls. Three peer outcomes:
 *   - success      → profile created
 *   - alreadyExists → backend returned 409; caller should redirect, not error
 *   - failure      → any other error class; caller shows error toast
 */
export type CreateProfileResult<T> =
	| { success: true; profile: T }
	| { success: false; alreadyExists: true }
	| { success: false; alreadyExists: false; error: string };

interface AxiosLikeError {
	response?: { status?: number; data?: { message?: string } };
}

function toCreateProfileResult<T>(error: unknown): CreateProfileResult<T> {
	const err = error as AxiosLikeError;
	const status = err.response?.status;
	if (status === 409) {
		return { success: false, alreadyExists: true };
	}
	// Surface non-409 failures so ops can see them — the toast text alone hides
	// 4xx/5xx and network errors behind a generic message.
	console.error("[onboarding] profile create failed", {
		status,
		message: err.response?.data?.message,
	});
	return {
		success: false,
		alreadyExists: false,
		error: err.response?.data?.message || "Failed to create profile",
	};
}

export interface Property {
	id: string;
	homeownerId: string;
	type: HomeownerPropertyType;
	/** The homeowner's words when `type` is "other". */
	typeOther?: string | null;
	status?: PropertyStatus | null;
	address?: string;
	city?: string;
	locality?: string;
	sizeSqft?: number;
	rooms?: {
		bedrooms?: number;
		bathrooms?: number;
		livingAreas?: number;
	};
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

export async function createHomeownerProfile(data: {
	name: string;
	email?: string;
	profilePictureUrl?: string;
	pincode?: string | null;
	goals?: HomeownerGoal[];
}): Promise<{ success: boolean; profile?: HomeownerProfile; error?: string }> {
	try {
		const response = await apiClient.post(`${API_BASE}/homeowners/me`, data);
		return { success: true, profile: response.data.data.profile };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to create profile",
		};
	}
}

export async function createDesignerProfile(data: {
	name: string;
	firmName?: string;
	bio?: string;
	services?: string[];
	serviceCities?: string[];
	styles?: string[];
	priceRangeMin?: number;
	priceRangeMax?: number;
	experienceYears?: number;
	portfolioImages?: string[];
	profilePictureUrl?: string;
	city?: string | null;
	state?: string | null;
	pincode?: string | null;
	projectTypes?: DesignerProjectType[];
	projectTypesOther?: string | null;
}): Promise<CreateProfileResult<DesignerProfile>> {
	try {
		const response = await apiClient.post(`${API_BASE}/designers/me`, data);
		return { success: true, profile: response.data.data.profile };
	} catch (error: unknown) {
		return toCreateProfileResult<DesignerProfile>(error);
	}
}

export async function createContractorProfile(data: {
	name: string;
	businessName?: string | null;
	profilePictureUrl?: string;
	trades?: string[];
	city?: string | null;
	state?: string | null;
	pincode?: string | null;
	serviceAreas?: string[];
	serviceRadiusKm?: number | null;
	projectTypes?: ContractorProjectType[];
	projectTypesOther?: string | null;
	priceRangeMin?: number | null;
	priceRangeMax?: number | null;
	experienceYears?: number;
	workPhotos?: string[];
}): Promise<CreateProfileResult<ContractorProfile>> {
	try {
		const response = await apiClient.post(`${API_BASE}/contractors/me`, data);
		return { success: true, profile: response.data.data.profile };
	} catch (error: unknown) {
		return toCreateProfileResult<ContractorProfile>(error);
	}
}

// Realtor profile DTO is centralized in @shared/types so the onboarding,
// realtor-feature, and any future realtor-side services all read the same
// shape. Re-export for ergonomic local imports.
import type { RealtorProfile } from "@shared/types";

export type { RealtorProfile };

export async function createRealtorProfile(data: {
	name: string;
	profileImageUrl?: string;
	agencyName?: string;
	serviceCities?: string[];
	primaryCity?: string;
	propertyTypes?: RealtorPropertyType[];
	clientTypes?: RealtorClientType[];
	serviceLocalities?: string[];
	specializations?: string[];
	yearsExperience?: number;
	languages?: string[];
}): Promise<{ success: boolean; profile?: RealtorProfile; error?: string }> {
	try {
		const response = await apiClient.post(`${API_BASE}/realtors/me`, data);
		return { success: true, profile: response.data.data.profile };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to create profile",
		};
	}
}

export async function createProperty(
	data: Omit<Property, "id" | "homeownerId">,
): Promise<{
	success: boolean;
	property?: Property;
	error?: string;
}> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/homeowners/me/properties`,
			data,
		);
		return { success: true, property: response.data.data.property };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to create property",
		};
	}
}
